import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, CheckCircle2, CalendarClock, AlertTriangle, ShieldCheck, Car, Split, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import TransactionDialog from "../components/TransactionDialog";
import { useSort, SortHead } from "../components/SortHead";
import api, { fmtNum, fmtDate, daysLate } from "../lib/api";

const ALL = "__all__";

const MONTHS_HR = ["siječanj", "veljača", "ožujak", "travanj", "svibanj", "lipanj", "srpanj", "kolovoz", "rujan", "listopad", "studeni", "prosinac"];
const monthLabel = (m) => `${MONTHS_HR[parseInt(m.slice(5)) - 1]} ${m.slice(0, 4)}.`;
const monthOptions = () => {
  const opts = [];
  const d = new Date();
  for (let i = 12; i >= -24; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
    opts.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return opts;
};
const TODAY = new Date().toISOString().slice(0, 10);

export default function TransactionsPage() {
  const navigate = useNavigate();
  const [txs, setTxs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ bank_id: ALL, type: ALL, category: ALL, status: ALL, vehicle: ALL, search: "", month: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filters.bank_id !== ALL) params.bank_id = filters.bank_id;
    if (filters.type !== ALL) params.type = filters.type;
    if (filters.category !== ALL) params.category = filters.category;
    if (filters.status !== ALL) params.status = filters.status;
    if (filters.vehicle !== ALL) params.vehicle = filters.vehicle;
    if (filters.search) params.search = filters.search;
    if (filters.month) params.month = filters.month;
    api.get("/transactions", { params }).then((r) => setTxs(r.data));
  }, [filters]);

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const bankName = (id) => banks.find((b) => b.id === id)?.short || "?";
  const txAccessors = {
    date: (t) => t.date,
    bank: (t) => bankName(t.bank_id),
    counterparty: (t) => (t.counterparty || "").toLowerCase(),
    category: (t) => t.category,
    amount: (t) => t.amount_eur,
    eur: (t) => t.amount_eur,
  };
  const { sorted: sortedTxs, sort, toggle } = useSort(txs, txAccessors);
  const hasFilters = filters.bank_id !== ALL || filters.type !== ALL || filters.category !== ALL || filters.status !== ALL || filters.vehicle !== ALL || filters.search || filters.month;

  const remove = async (id) => {
    if (!window.confirm("Obrisati transakciju?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Transakcija obrisana");
    load();
  };

  const realize = async (id) => {
    await api.post(`/transactions/${id}/realize`);
    toast.success("Označeno kao ostvareno");
    load();
  };

  const notDuplicate = async (id) => {
    const r = await api.post(`/transactions/${id}/not-duplicate`);
    toast.success(`Zabilježeno: nije duplikat (potvrdio ${r.data.confirmed_by})`);
    load();
  };

  const [linkTx, setLinkTx] = useState(null);
  const [linkSel, setLinkSel] = useState([]);
  const [linkPlans, setLinkPlans] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);

  const openLink = async (t) => {
    const r = await api.get("/transactions", { params: { status: "planirano", type: t.type, limit: 500 } });
    setLinkPlans(r.data.filter((p) => p.currency === t.currency));
    setLinkSel([]);
    setLinkTx(t);
  };
  const toggleLinkPlan = (pid) => setLinkSel((sel) => (sel.includes(pid) ? sel.filter((x) => x !== pid) : [...sel, pid]));
  const confirmLink = async () => {
    setLinkBusy(true);
    try {
      const r = await api.post(`/transactions/${linkTx.id}/link-plans`, { plan_ids: linkSel });
      toast.success(`Povezano s ${r.data.linked} planiranih — kontrola iznosa: ${fmtNum(r.data.amount, r.data.currency === "EUR" ? "€" : "KM")} pokriveno ✓`);
      setLinkTx(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri povezivanju");
    } finally {
      setLinkBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="transactions-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Evidencija</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Transakcije</h1>
        </div>
        <Button data-testid="add-transaction-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Nova transakcija
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 bg-white border border-[#E2E8F0] rounded-sm p-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input data-testid="tx-search-input" className="pl-9" placeholder="Pretraži sve: primatelj, banka, račun, iznos, kategorija, napomena..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
        </div>
        <Select value={filters.month || ALL} onValueChange={(v) => setFilters((f) => ({ ...f, month: v === ALL ? "" : v }))}>
          <SelectTrigger data-testid="filter-month-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Svi mjeseci</SelectItem>
            {monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.bank_id} onValueChange={(v) => setFilters((f) => ({ ...f, bank_id: v }))}>
          <SelectTrigger data-testid="filter-bank-select" className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Sve banke</SelectItem>
            {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.short || b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v }))}>
          <SelectTrigger data-testid="filter-type-select" className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Svi tipovi</SelectItem>
            <SelectItem value="placanje">Plaćanje</SelectItem>
            <SelectItem value="naplata">Naplata</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger data-testid="filter-status-select" className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Svi statusi</SelectItem>
            <SelectItem value="ostvareno">Ostvareno</SelectItem>
            <SelectItem value="planirano">Planirano</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="filter-category-select" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Sve kategorije</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {vehicles.length > 0 && (
          <Select value={filters.vehicle} onValueChange={(v) => setFilters((f) => ({ ...f, vehicle: v }))}>
            <SelectTrigger data-testid="filter-vehicle-select" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value={ALL}>Sva vozila</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.plate}>{v.plate}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {hasFilters && (
          <button data-testid="clear-filters-btn" onClick={() => setFilters({ bank_id: ALL, type: ALL, category: ALL, status: ALL, vehicle: ALL, search: "", month: "" })} className="text-sm text-[#475569] hover:text-[#DC2626] underline underline-offset-2 transition-colors duration-200">
            Očisti
          </button>
        )}
        <p className="ml-auto text-xs text-[#64748B]" data-testid="tx-count">{txs.length} transakcija</p>
      </div>

      <div className="md:hidden space-y-2" data-testid="transactions-cards">
        {txs.length === 0 && (
          <p className="text-center text-sm text-[#64748B] py-8 bg-white border border-[#E2E8F0] rounded-sm">Nema transakcija. Dodajte prvu ili uvezite izvod.</p>
        )}
        {sortedTxs.map((t) => (
          <div key={t.id} data-testid={`tx-card-${t.id}`} className={`bg-white border rounded-sm p-3 space-y-2 ${t.status === "planirano" ? (t.date < TODAY ? "border-[#DC2626]/40 bg-[#DC2626]/5" : "border-[#D97706]/40 bg-[#D97706]/5") : "border-[#E2E8F0]"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <button onClick={() => t.counterparty && navigate(`/partneri?name=${encodeURIComponent(t.counterparty)}`)} className="font-semibold text-sm text-[#0F172A] truncate max-w-full text-left hover:text-[#0D9488] transition-colors duration-200">
                  {t.counterparty || t.category}
                </button>
                <p className="text-xs text-[#475569] mt-0.5">{fmtDate(t.date)} · {bankName(t.bank_id)} · {t.category}{t.vehicle ? ` · vozilo ${t.vehicle}` : ""}</p>
                {t.counterparty === "Polog pazara" && (
                  <p className="text-[11px] font-medium text-[#0D9488] mt-0.5">Polog u banku: {bankName(t.bank_id)}</p>
                )}
                {(t.recipient_bank || t.recipient_account) && (
                  <p className="text-[11px] text-[#64748B] truncate mt-0.5">{[t.recipient_bank, t.recipient_account].filter(Boolean).join(" · ")}</p>
                )}
                {t.note && <p className="text-[11px] italic text-[#94A3B8] truncate mt-0.5">{t.note}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className={`tabular-nums font-semibold text-sm ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                  {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                </p>
                {t.currency !== "EUR" && <p className="text-[11px] text-[#64748B] tabular-nums">≈ {fmtNum(t.amount_eur, "€")}</p>}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                {t.status === "planirano" && t.date < TODAY ? (
                  <Badge className="rounded-sm text-[11px] font-medium bg-[#DC2626]/15 text-[#DC2626] hover:bg-[#DC2626]/15 gap-1"><CalendarClock className="w-3 h-3" /> Dospjelo · kasni {daysLate(t.date)} d</Badge>
                ) : t.status === "planirano" ? (
                  <Badge className="rounded-sm text-[11px] font-medium bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 gap-1"><CalendarClock className="w-3 h-3" /> Planirano</Badge>
                ) : null}
                {t.dup_suspect && (
                  <Badge className="rounded-sm text-[11px] font-medium bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 gap-1"><AlertTriangle className="w-3 h-3" /> Mogući duplikat</Badge>
                )}
              </div>
              <div className="flex gap-0.5 items-center shrink-0">
                {t.dup_suspect && (
                  <button data-testid={`card-not-duplicate-${t.id}`} onClick={() => notDuplicate(t.id)} className="p-2 text-[#D97706] hover:text-[#0D9488] transition-colors duration-200" title="Nije duplikat"><ShieldCheck className="w-4 h-4" /></button>
                )}
                {t.status === "planirano" && (
                  <button data-testid={`card-realize-${t.id}`} onClick={() => realize(t.id)} className="p-2 text-[#D97706] hover:text-[#0D9488] transition-colors duration-200" title="Označi kao ostvareno"><CheckCircle2 className="w-4 h-4" /></button>
                )}
                {t.status !== "planirano" && (
                  <button data-testid={`card-link-${t.id}`} onClick={() => openLink(t)} className="p-2 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200"><Split className="w-4 h-4" /></button>
                )}
                <button data-testid={`card-edit-${t.id}`} onClick={() => { setEditing(t); setDialogOpen(true); }} className="p-2 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`card-delete-${t.id}`} onClick={() => remove(t.id)} className="p-2 text-[#64748B] hover:text-[#DC2626] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
              <SortHead label="Datum" sortKey="date" sort={sort} onToggle={toggle} />
              <SortHead label="Naš račun" sortKey="bank" sort={sort} onToggle={toggle} />
              <TableHead>Tip</TableHead>
              <SortHead label={<span title="Za naplatu: platitelj (tko nama plaća) · za plaćanje: primatelj (kome mi plaćamo)">Primatelj / platitelj</span>} sortKey="counterparty" sort={sort} onToggle={toggle} />
              <TableHead>Opis</TableHead>
              <SortHead label="Kategorija" sortKey="category" sort={sort} onToggle={toggle} />
              <SortHead label="Iznos" sortKey="amount" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="EUR" sortKey="eur" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="transactions-table-body">
            {txs.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-[#64748B] py-10">Nema transakcija. Dodajte prvu ili uvezite izvod.</TableCell></TableRow>
            )}
            {sortedTxs.map((t) => (
              <TableRow key={t.id} className={`hover:bg-[#F8FAFC] transition-colors duration-200 ${t.status === "planirano" ? (t.date < TODAY ? "bg-[#DC2626]/5" : "bg-[#D97706]/5") : ""}`} data-testid={`tx-row-${t.id}`}>
                <TableCell className="tabular-nums text-sm whitespace-nowrap">
                  {fmtDate(t.date)}
                  {t.status === "planirano" && t.type === "naplata" && t.expected_date && t.expected_date !== t.date && (
                    <span data-testid={`tx-expected-${t.id}`} className="block text-[11px] text-[#D97706] font-medium">očekivano {fmtDate(t.expected_date)}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{bankName(t.bank_id)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={`rounded-sm text-xs font-medium ${t.type === "naplata" ? "bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/10" : "bg-[#DC2626]/10 text-[#DC2626] hover:bg-[#DC2626]/10"}`}>
                      {t.type === "naplata" ? "Naplata" : "Plaćanje"}
                    </Badge>
                    {t.status === "planirano" && t.date < TODAY ? (
                      <Badge data-testid={`overdue-badge-${t.id}`} className="rounded-sm text-xs font-medium bg-[#DC2626]/15 text-[#DC2626] hover:bg-[#DC2626]/15 gap-1">
                        <CalendarClock className="w-3 h-3" /> Dospjelo · kasni {daysLate(t.date)} d
                      </Badge>
                    ) : t.status === "planirano" ? (
                      <Badge data-testid={`planned-badge-${t.id}`} className="rounded-sm text-xs font-medium bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 gap-1">
                        <CalendarClock className="w-3 h-3" /> Planirano
                      </Badge>
                    ) : null}
                    {t.dup_suspect && (
                      <Badge data-testid={`dup-suspect-badge-${t.id}`} className="rounded-sm text-xs font-medium bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 gap-1">
                        <AlertTriangle className="w-3 h-3" /> Mogući duplikat
                      </Badge>
                    )}
                    {!t.dup_suspect && t.dup_confirmed_by && (
                      <span data-testid={`dup-confirmed-${t.id}`} title={`Potvrdio da nije duplikat: ${t.dup_confirmed_by}, ${t.dup_confirmed_at ? new Date(t.dup_confirmed_at).toLocaleDateString("hr-HR") : ""}`} className="text-[#0D9488]">
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm font-semibold max-w-[260px]">
                  {t.counterparty ? (
                    <button
                      data-testid={`partner-link-${t.id}`}
                      onClick={() => navigate(`/partneri?name=${encodeURIComponent(t.counterparty)}`)}
                      className="hover:text-[#0D9488] hover:underline underline-offset-2 transition-colors duration-200 truncate max-w-full text-left block"
                      title="Otvori karticu partnera"
                    >
                      {t.counterparty}
                    </button>
                  ) : ""}
                  {t.counterparty === "Polog pazara" && (
                    <span data-testid={`tx-polog-bank-${t.id}`} className="block text-[11px] font-medium text-[#0D9488] mt-0.5">
                      Polog u banku: {bankName(t.bank_id)}
                    </span>
                  )}
                  {t.worker_name && (
                    <span data-testid={`tx-worker-${t.id}`} className="block text-[11px] font-medium text-[#475569] mt-0.5">
                      radnik: {t.worker_name}
                    </span>
                  )}
                  {(t.recipient_bank || t.recipient_account) && (
                    <span className="block text-[11px] font-normal text-[#475569] truncate" title={[t.recipient_bank, t.recipient_account].filter(Boolean).join(" · ")}>
                      {[t.recipient_bank, t.recipient_account].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {t.vehicle && (
                    <span data-testid={`tx-vehicle-${t.id}`} className="flex items-center gap-1 text-[11px] font-normal text-[#0D9488] mt-0.5">
                      <Car className="w-3 h-3" /> {t.vehicle}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-[#475569] max-w-[220px]">
                  <span data-testid={`tx-note-${t.id}`} className="block truncate" title={t.note || ""}>{t.note || "—"}</span>
                </TableCell>
                <TableCell className="text-sm text-[#475569]">{t.category}</TableCell>
                <TableCell className={`text-right tabular-nums font-semibold text-sm whitespace-nowrap ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                  {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm text-[#475569]">{fmtNum(t.amount_eur)}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end items-center">
                    {t.dup_suspect && (
                      <button data-testid={`not-duplicate-btn-${t.id}`} onClick={() => notDuplicate(t.id)} title="Potvrdi da NIJE duplikat (bilježi se tko je potvrdio)" className="p-2 text-[#D97706] hover:text-[#0D9488] transition-colors duration-200">
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    )}
                    {t.status === "planirano" && (
                      <button data-testid={`realize-tx-${t.id}`} onClick={() => realize(t.id)} title="Označi kao ostvareno" className="p-2 text-[#D97706] hover:text-[#0D9488] transition-colors duration-200">
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                    )}
                    {t.status !== "planirano" && (
                      <button data-testid={`link-tx-${t.id}`} onClick={() => openLink(t)} title="Poveži s planiranim stavkama — npr. polog pazara koji pokriva više planova" className="p-2 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200">
                        <Split className="w-4 h-4" />
                      </button>
                    )}
                    <button data-testid={`edit-tx-${t.id}`} onClick={() => { setEditing(t); setDialogOpen(true); }} title="Uredi" className="p-2 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-tx-${t.id}`} onClick={() => remove(t.id)} title="Obriši" className="p-2 text-[#64748B] hover:text-[#DC2626] transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TransactionDialog open={dialogOpen} onOpenChange={setDialogOpen} banks={banks} categories={categories} editing={editing} onSaved={load} />

      {linkTx && (() => {
        const amount = linkTx.amount;
        const cur = linkTx.currency === "EUR" ? "€" : "KM";
        const selPlans = linkSel.map((id) => linkPlans.find((p) => p.id === id)).filter(Boolean);
        const selSum = +selPlans.reduce((s, p) => s + p.amount, 0).toFixed(2);
        const diff = +(selSum - amount).toFixed(2);
        const last = selPlans[selPlans.length - 1];
        const overAllocated = last && selSum - last.amount >= amount - 0.01;
        const canConfirm = selPlans.length >= 1 && diff >= -0.01 && !overAllocated;
        return (
          <Dialog open onOpenChange={(o) => !o && setLinkTx(null)}>
            <DialogContent data-testid="link-dialog" className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><Split className="w-4 h-4 text-[#0D9488]" /> Poveži s planiranim stavkama</DialogTitle>
              </DialogHeader>
              <div className="bg-[#0D9488]/5 border border-[#0D9488]/25 rounded-sm px-3 py-2 text-sm" data-testid="link-tx-info">
                <span className="font-semibold text-[#0F172A]">Ostvareno na izvodu:</span>{" "}
                {fmtDate(linkTx.date)} · {linkTx.counterparty || linkTx.category} · <span className="font-bold tabular-nums text-[#0D9488]">{fmtNum(amount, cur)}</span>
                <p className="text-xs text-[#475569] mt-1">Odabrani planovi postaju ostvareni (s datumom i bankom s izvoda), a ova stavka se zamjenjuje njima — saldo ostaje isti. Ako iznos ne pokriva zadnji plan u cijelosti, on se dijeli.</p>
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="link-plan-list">
                {linkPlans.length === 0 && <p className="text-sm text-[#64748B]">Nema otvorenih planova istog tipa i valute.</p>}
                {linkPlans.map((p) => {
                  const selIdx = linkSel.indexOf(p.id);
                  return (
                    <div key={p.id} data-testid={`link-plan-${p.id}`} onClick={() => toggleLinkPlan(p.id)} className={`flex items-center gap-3 border rounded-sm px-3 py-2 cursor-pointer transition-colors duration-150 ${selIdx >= 0 ? "border-[#0D9488]/50 bg-[#0D9488]/5" : "border-[#E2E8F0] hover:bg-[#F8FAFC]"}`}>
                      <Checkbox checked={selIdx >= 0} className="pointer-events-none" tabIndex={-1} />
                      {selIdx >= 0 && <span className="w-5 h-5 rounded-sm bg-[#0D9488] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{selIdx + 1}</span>}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-[#0F172A] truncate">{p.counterparty || p.category}</span>
                        <span className="block text-xs text-[#64748B] truncate">{fmtDate(p.date)}{p.note ? ` · ${p.note}` : ""}</span>
                      </span>
                      <span className="font-semibold tabular-nums text-sm shrink-0">{fmtNum(p.amount, p.currency === "EUR" ? "€" : "KM")}</span>
                    </div>
                  );
                })}
              </div>
              <div data-testid="link-summary" className={`rounded-sm px-3 py-2.5 text-sm border ${!selPlans.length ? "bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]" : overAllocated || diff < -0.01 ? "bg-[#DC2626]/10 border-[#DC2626]/40 text-[#7A3830]" : diff > 0.01 ? "bg-[#D97706]/10 border-[#D97706]/40 text-[#92400E]" : "bg-[#2F7D4F]/10 border-[#2F7D4F]/40 text-[#2F7D4F]"}`}>
                <p className="font-semibold tabular-nums">Izvod {fmtNum(amount, cur)} · odabrano {selPlans.length} {selPlans.length === 1 ? "plan" : "planova"} = {fmtNum(selSum, cur)}</p>
                {!selPlans.length ? (
                  <p className="text-xs mt-0.5">Odaberite planove redom kojim ih uplata/isplata pokriva.</p>
                ) : diff < -0.01 ? (
                  <p className="text-xs mt-0.5">Kontrola iznosa: nedostaje {fmtNum(-diff, cur)} — zbroj planova mora pokriti cijeli iznos s izvoda.</p>
                ) : overAllocated ? (
                  <p className="text-xs mt-0.5">Previše odabrano — zadnji odabrani plan ne bi bio realiziran ni djelomično. Maknite ga.</p>
                ) : diff > 0.01 ? (
                  <p className="text-xs mt-0.5">Zadnji odabrani ({last.counterparty || last.category}) bit će DJELOMIČNO realiziran: {fmtNum(+(last.amount - diff).toFixed(2), cur)} od {fmtNum(last.amount, cur)} — ostaje otvoren plan {fmtNum(diff, cur)}.</p>
                ) : (
                  <p className="text-xs mt-0.5">Kontrola iznosa: zbroj se točno poklapa s izvodom. ✓</p>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button data-testid="link-cancel-btn" variant="outline" onClick={() => setLinkTx(null)} className="rounded-sm border-[#CBD5E1]"><X className="w-4 h-4 mr-1" /> Odustani</Button>
                <Button data-testid="link-confirm-btn" disabled={!canConfirm || linkBusy} onClick={confirmLink} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
                  <Check className="w-4 h-4 mr-1" /> Poveži ({selPlans.length})
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
